import { FC, useState, useEffect } from 'react';
import cn from 'classnames';
import {
  useTrackToggle,
  useConnectionState,
  useLocalParticipant
} from '@livekit/components-react';
import { ToggleSource } from "@livekit/components-core";
import { ConnectionState, Track } from 'livekit-client';
import Icon from '../../../components/Icon';
import { Icon as IconType } from '../../../components/Icon/Icon.interface';
import styles from './FunctionalButtons.module.css';

interface TrackToggleButtonProps {
  trackSource: ToggleSource;
  iconName: IconType;
  tooltipEnabled: string;
  tooltipDisabled: string;
}

const TrackToggleButton: FC<TrackToggleButtonProps> = ({
  trackSource,
  iconName,
  tooltipEnabled,
  tooltipDisabled,
}) => {
  const [error, setError] = useState<string | null>(null);
  const connectionState = useConnectionState();
  const isConnected = connectionState === ConnectionState.Connected;
  const { localParticipant } = useLocalParticipant();

  const { enabled, toggle, pending } = useTrackToggle({
    source: trackSource
  });

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleToggle = async () => {
    if (!isConnected || !localParticipant) {
      setError('Невозможно включить устройство: нет подключения к комнате');
      return;
    }

    setError(null);

    try {
      if (trackSource === Track.Source.ScreenShare) {
        await toggle(undefined, {
          audio: true
        });
      } else {
        await toggle();
      }
    } catch (err) {
      const error = err as Error;

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setError('Доступ к устройству запрещен. Пожалуйста, разрешите доступ в настройках браузера.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setError('Устройство не найдено. Проверьте подключение камеры/микрофона.');
      } else if (error.message?.includes('not connected')) {
        setError('Невозможно включить устройство: нет подключения к комнате');
      } else {
        setError(error.message || 'Неизвестная ошибка');
      }
    }
  };

  return (
    <li
      role="button"
      className={cn(
        styles["functional-buttons__button"],
        { [styles["functional-buttons__button--active"]]: enabled },
        { [styles["functional-buttons__button--loading"]]: pending },
        { [styles["functional-buttons__button--disabled"]]: !isConnected }
      )}
      onClick={isConnected ? handleToggle : undefined}
      title={
        !isConnected
          ? 'Ожидание подключения к комнате...'
          : error || (enabled ? tooltipEnabled : tooltipDisabled)
      }
      aria-label={enabled ? tooltipEnabled : tooltipDisabled}
      aria-disabled={pending || !isConnected}
    >
      <div
        className={cn(
          styles["functional-buttons__icon-wrapper"],
          { [styles["functional-buttons__icon-wrapper--active"]]: enabled },
          { [styles["functional-buttons__icon-wrapper--error"]]: !!error },
          { [styles["functional-buttons__icon-wrapper--disabled"]]: !isConnected }
        )}
      >
        {pending ? (
          <span className={styles["functional-buttons__loading-indicator"]}></span>
        ) : (
          <Icon name={iconName} />
        )}
      </div>
      {error && (
        <div className={styles["functional-buttons__error-tooltip"]}>
          {error}
        </div>
      )}
    </li>
  );
};

export default TrackToggleButton;